import { useCallback, useEffect, useRef, useState } from 'react';

/** The sidebar folds into the rail after this long without interaction. */
export const SIDEBAR_IDLE_MS = 10_000;
/** Remembers, per browser, that the operator pinned the sidebar open. */
export const PIN_KEY = 'monitoring:sidebarPinned';

function readPinned(): boolean {
  try {
    return localStorage.getItem(PIN_KEY) === '1';
  } catch {
    return false;
  }
}

function writePinned(pinned: boolean) {
  try {
    if (pinned) localStorage.setItem(PIN_KEY, '1');
    else localStorage.removeItem(PIN_KEY);
  } catch {
    /* storage unavailable: the pin lasts for this page only */
  }
}

export interface CollapsingSidebar {
  expanded: boolean;
  pinned: boolean;
  expand: () => void;
  collapse: () => void;
  togglePin: () => void;
  /** Activity that is not hover or focus (a tap, wheel, list scroll): restarts the countdown. */
  touch: () => void;
}

/**
 * The monitoring sidebar's open/closed state (desktop and tablet). It starts
 * open and folds after SIDEBAR_IDLE_MS while `enabled`, not pinned and not
 * `busy` (pointer over it, keyboard focus or typing inside, a menu or dialog
 * open). Becoming busy cancels the countdown; becoming idle restarts it, and
 * so does `touch()` (touch devices have no hover to hold it open). Every
 * collapse, by the timer or by `collapse()`, first calls `onCollapse`.
 */
export function useCollapsingSidebar({
  enabled,
  busy,
  onCollapse,
}: {
  enabled: boolean;
  busy: boolean;
  onCollapse?: () => void;
}): CollapsingSidebar {
  const [expanded, setExpanded] = useState(true);
  const [pinned, setPinned] = useState(readPinned);
  const onCollapseRef = useRef(onCollapse);
  useEffect(() => {
    onCollapseRef.current = onCollapse;
  }, [onCollapse]);
  // Restarts the running countdown, if any (no re-render per scroll event).
  const restartRef = useRef<() => void>(() => undefined);

  const collapse = useCallback(() => {
    onCollapseRef.current?.();
    setExpanded(false);
  }, []);

  useEffect(() => {
    if (!enabled || !expanded || pinned || busy) return;
    let timer = window.setTimeout(collapse, SIDEBAR_IDLE_MS);
    restartRef.current = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(collapse, SIDEBAR_IDLE_MS);
    };
    return () => {
      window.clearTimeout(timer);
      restartRef.current = () => undefined;
    };
  }, [enabled, expanded, pinned, busy, collapse]);

  const expand = useCallback(() => {
    setExpanded(true);
  }, []);
  const touch = useCallback(() => {
    restartRef.current();
  }, []);
  const togglePin = useCallback(() => {
    const next = !pinned;
    writePinned(next);
    setPinned(next);
    setExpanded(true);
  }, [pinned]);

  return { expanded, pinned, expand, collapse, togglePin, touch };
}
